import React from 'react';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { CompanySelector } from '@/components/company/CompanySelector';
import { useCompany } from '@/context/CompanyContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { PremiumBanner } from '@/components/auth/PremiumBanner';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { SkipLink } from '@/components/common/SkipLink';
import { LiveAnnouncer } from '@/components/common/LiveAnnouncer';

interface MainLayoutProps {
  children: React.ReactNode;
  title: string;
  actions?: React.ReactNode;
}
export const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  title,
  actions
}) => {
  const { currentCompany } = useCompany();
  const { hasAccess } = useSubscription();

  return (
    <>
      <SkipLink />
      <LiveAnnouncer />
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <AppSidebar />
          
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Header responsive */}
            <header role="banner" className="border-b bg-background/95 text-foreground backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 shrink-0">
            <div className="flex h-12 sm:h-14 lg:h-16 items-center gap-2 sm:gap-4 px-3 sm:px-4 lg:px-6">
              <SidebarTrigger className="shrink-0" />
              
              <div className="flex-1 flex items-center justify-between min-w-0">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-4 min-w-0 flex-1">
                  <h1 className="text-base sm:text-lg lg:text-xl font-semibold truncate">
                    {title}
                  </h1>
                  {currentCompany && (
                    <span className="text-xs sm:text-sm text-muted-foreground truncate">
                      {currentCompany.name}
                    </span>
                  )}
                  <div className="hidden sm:block">
                    <CompanySelector />
                  </div>
                </div>
                
                <div className="flex items-center gap-1 sm:gap-2 lg:gap-4 shrink-0">
                  <div className="sm:hidden">
                    <CompanySelector />
                  </div>
                  {actions && (
                    <div className="flex items-center gap-1 sm:gap-2">
                      {actions}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Contenu principal */}
          <main 
            id="main-content" 
            role="main" 
            aria-label="Contenu principal"
            className={`flex-1 overflow-auto ${!hasAccess ? 'pb-20 sm:pb-24' : ''}`}
          >
            <div className="p-3 sm:p-4 lg:p-6">
              {children}
            </div>
          </main>
        </div>
        
        <PremiumBanner />
      </div>
    </SidebarProvider>
    </>
  );
};